import React, { useState, useEffect } from "react";
import { ThemeProvider } from "@emotion/react";
import theme from "../components/theme";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import Dashboard from "./Dashboard";
import DownloadPage from "./DownloadPage";
import ApplicationsPage from "./ApplicationsPage";
import ApplicationDetailsPage from "./ApplicationDetailsPage";
import ProfilePage from "./Profile";
import WelcomePage from "./WelcomePage";
import LoadingPage from "./LoadingPage";
import { CssBaseline } from "@mui/material";
import { loginRequest } from "../authConfig";
import {
  useMsal,
  useIsAuthenticated,
  MsalAuthenticationTemplate,
} from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  InteractionType,
  InteractionStatus,
} from "@azure/msal-browser";
import axiosInstance from "./api/base-url";
import api from "./api/base-url";
import WarningPage from "./WarningPage";
import { useTranslation } from "react-i18next";

/* <App /> is the entry point of the web application. 
It is a wrapper for all the web pages.*/

function App() {
  return (
    <div>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="App">
          <Pages />
        </div>
      </ThemeProvider>
    </div>
  );
}

/* <DashboardWrapper />  is a component wrapping all the pages requiring authentication 
(all pages except <DownloadPage />). 
<DownloadPage /> authentication is managed individually, 
since links for download can be public or not and also corporate users not included in AD group can access. */

function DashboardWrapper() {
  const { instance, accounts, inProgress } = useMsal();

  const [acquiringToken, setAcquiringToken] = useState(false);

  const isAuthenticated = useIsAuthenticated();
  const { t } = useTranslation();

  const [isErrorUser, setIsUserError] = useState(false);

  /* API call to get logged-in User Info */
  const getUserInfo = async () => {
    try {
      const response = await api.get("users/me");
      console.log(response.data);
      localStorage.setItem("Role", response.data.status);
    } catch (err: any) {
      setIsUserError(true);
      if (err.response) {
        setIsUserError(true);
        //Not in 200 response range
        console.log(err.response.data);
        console.log(err.response.status);
      } else {
        console.log(`Error: ${err.message}`);
      }
      setIsUserError(true);
    }
  };

  /* To authenticate users: 
    -   if no user is authenticated ("IdToken" === null), 
    a login request is performed --> a token to be inserted in the header 
    of the requests is acquired.
  */
  useEffect(() => {
    if (isAuthenticated && localStorage.getItem("IdToken") === null) {
      setAcquiringToken(true);

      const request = {
        ...loginRequest,
        account: accounts[0],
      };

      instance
        .acquireTokenSilent(request)
        .then((response) => {
          localStorage.setItem("Token", response.accessToken);
          localStorage.setItem("IdToken", response.idToken);
          localStorage.setItem(
            "Name",
            JSON.parse(atob(response.accessToken.split(".")[1]))["given_name"]
          );
          localStorage.setItem(
            "Surname",
            JSON.parse(atob(response.accessToken.split(".")[1]))["family_name"]
          );

          axiosInstance.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${response.idToken}`;

          setAcquiringToken(false);
          getUserInfo();
        })
        .catch(async (e) => {
          if (e instanceof InteractionRequiredAuthError) {
            await instance.acquireTokenRedirect(request);
          }
        });
    }
  }, [isAuthenticated, accounts, instance]);

  if (
    inProgress !== InteractionStatus.None ||
    (isAuthenticated && acquiringToken)
  )
    return <LoadingPage />;
  else if (!isAuthenticated) return <WelcomePage />;
  else if (isAuthenticated && isErrorUser)
    return (
      <WarningPage
        title={t("Unauthorized")}
        body={t("You are not allowed to see this page content.")}
      />
    );
  else
    return (
      <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
        <Outlet />
      </MsalAuthenticationTemplate>
    );
}

/* List of all the pages and related routes */
function Pages() {
  const { t } = useTranslation();

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <div>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<DashboardWrapper />}>
                <Route path="/" element={<Dashboard />}></Route>
                <Route
                  path="applications"
                  element={<ApplicationsPage />}
                ></Route>
                <Route
                  path="applications/:appID"
                  element={<ApplicationDetailsPage />}
                ></Route>
                <Route path="profile" element={<ProfilePage />}></Route>
                <Route
                  path="*"
                  element={
                    <WarningPage
                      title={t("Sorry, page not found!")}
                      body={t(
                        "Sorry, we couldn’t find the page you’re looking for. Perhaps you’ve mistyped the URL? Be sure to check your spelling."
                      )}
                    />
                  }
                ></Route>
              </Route>
              <Route
                path=":appID/download/:uuid"
                element={<DownloadPage />}
              ></Route>
              <Route
                path=":appID/download/auth/:uuid"
                element={<DownloadPage auth={true} />}
              ></Route>
            </Routes>
          </BrowserRouter>
        </div>
      </ThemeProvider>
    </div>
  );
}

export default App;
